import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const GOAL_LABELS = {
  lose_weight: 'Lose Weight',
  build_muscle: 'Build Muscle',
  improve_endurance: 'Improve Endurance',
  increase_flexibility: 'Increase Flexibility',
  general_fitness: 'General Fitness',
  sports_performance: 'Sports Performance',
};

const DIET_LABELS = {
  no_restriction: 'No Restriction',
  vegetarian: 'Vegetarian',
  vegan: 'Vegan',
  keto: 'Keto',
  paleo: 'Paleo',
  gluten_free: 'Gluten-Free',
  dairy_free: 'Dairy-Free',
};

export default function CompletionScreen({ workoutData, goalData, dietData }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const summaryItems = [
    {
      icon: 'Dumbbell',
      label: 'Workout Focus',
      value: workoutData?.workoutTypes?.length
        ? workoutData?.workoutTypes?.slice(0, 2)?.map((t) => t?.replace('_', ' '))?.join(', ')
        : 'Set',
      color: 'var(--color-secondary)',
    },
    {
      icon: 'Target',
      label: 'Primary Goal',
      value: GOAL_LABELS?.[goalData?.primaryGoal] || 'General Fitness',
      color: 'var(--color-success)',
    },
    {
      icon: 'Salad',
      label: 'Diet Plan',
      value: DIET_LABELS?.[dietData?.dietaryPreference] || 'No Restriction',
      color: 'var(--color-warning)',
    },
  ];

  return (
    <div
      className={`flex flex-col items-center text-center transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* Success Animation */}
      <div className="relative mb-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center shadow-lg animate-pulse">
          <Icon name="CheckCircle" size={48} color="#fff" strokeWidth={1.5} />
        </div>
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-[var(--color-success)] flex items-center justify-center shadow-md">
          <Icon name="Sparkles" size={14} color="#fff" />
        </div>
      </div>
      <h2 className="text-3xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)] mb-2">
        You are all set!
      </h2>
      <p className="text-[var(--color-muted-foreground)] font-[var(--font-caption)] max-w-sm mb-8">
        Your personalized fitness journey is ready. Here is a summary of your setup.
      </p>
      {/* Summary Cards */}
      <div className="w-full grid grid-cols-2 gap-3 mb-8">
        {summaryItems?.map((item) => (
          <div
            key={item?.label}
            className="flex items-center gap-3 p-4 rounded-[var(--radius-md)] bg-[var(--color-card)] border border-[var(--color-border)] text-left"
          >
            <div
              className="w-9 h-9 rounded-[var(--radius-sm)] flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${item?.color}18` }}
            >
              <Icon name={item?.icon} size={18} color={item?.color} strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{item?.label}</p>
              <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate capitalize">{item?.value}</p>
            </div>
          </div>
        ))}
      </div>
      {/* CTA */}
      <button
        onClick={() => navigate('/member-portal')}
        className="flex items-center gap-2 px-8 py-3.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-md)] font-semibold font-[var(--font-caption)] text-base hover:opacity-90 active:scale-95 transition-all duration-200 shadow-md w-full sm:w-auto justify-center"
      >
        <Icon name="LayoutDashboard" size={18} color="#fff" />
        Go to Member Portal
      </button>
      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-4">
        You can update these preferences anytime from your profile settings.
      </p>
    </div>
  );
}
